// Polls a job-status endpoint until the job finishes or errors.
//
// Why this exists: the bulk CSV imports run for minutes. Held open as one HTTP
// request a gateway cuts the connection, and the UI reports "cannot reach the
// server" while the import is still running happily. So the POST returns a job
// id immediately and the browser watches progress through here.
//
// Design notes:
//   - Chained setTimeout, NEVER setInterval. The next poll is scheduled only
//     after the previous one settles, so a slow response cannot pile up
//     overlapping requests.
//   - Stops itself on unmount. The pages using this have a Cancel button that
//     navigates away mid-job; a poll landing after that is a setState-on-
//     unmounted warning plus a request nobody is waiting for.
//   - A single failed request does not end the job. Transient blips are
//     tolerated up to maxConsecutiveErrors, counter reset on any success.
//   - It caps SILENCE, not duration. Imports can run 30+ minutes, so a
//     wall-clock limit is either too short for real jobs or too slow to catch a
//     dead one. Instead the counters are watched: while they move, polling
//     continues indefinitely; when they stop for stallMs, the job is called
//     stalled. maxDurationMs remains only as a pathological backstop.
//   - The interval widens as the job ages (see BACKOFF), so a long import does
//     not cost hundreds of DB-backed requests.
//
// Generic on purpose: pass buildUrl and it works for any job endpoint that
// answers { error, message, job: { ..., status } }. Used by
// ReIssuePassCreation; BulkPassCreation can adopt it unchanged.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { describeError } from './errorUtils';

export const JOB_STATUS = { PROCESSING: 0, FINISHED: 1, ERROR: 2 };

// Why a job stopped being watched. Callers need this to distinguish "the server
// told us it failed" from "we lost sight of it" — those look identical in a
// message string but mean opposite things.
//
// JOB_ERROR is the server's own verdict (status 2): a logical error in the
// backend file operation, or a failure talking to Litecard. The server ran, hit
// a real problem, and recorded it — so an error file exists.
//
// NETWORK and TIMEOUT are the CLIENT's classification. The server never got to
// report anything and the job is very likely still running, so there is no error
// file and callers must not imply a failure that has not happened.
export const JOB_FAILURE = {
  JOB_ERROR:    'job-error',
  SERVER_ERROR: 'server-error',  // top-level error flag on the status response
  BAD_RESPONSE: 'bad-response',  // no job object, or a status code we don't know
  NETWORK:      'network',
  STALLED:      'stalled',       // counters stopped moving; job may have died
  TIMEOUT:      'timeout',       // absolute backstop only
};

// The server's `process` codes on get-active-jobs, one per bulk operation.
// A page uses its own code to find its job in the list.
export const JOB_PROCESS = {
  BULK_PASS:              'CBP',  // Create Bulk Pass — BulkPassCreation
  REISSUE_PASS:           'RIP',  // Reissue Passes   — ReIssuePassCreation
  // Bulk coupon assignment was one code (BPA) covering both promotion types.
  // Split so each page adopts only its own job: matching on the code alone, the
  // consumer page would have picked up a membership run after a refresh and
  // shown its counters as its own — real numbers, wrong page. The server's lock
  // is per (club, process), so these two also run concurrently now.
  MEMBERSHIP_COUPON_BULK: 'BPC',  // Multiple Members  — promotion type 2
  CONSUMER_COUPON_BULK:   'BPP',  // Multiple Consumer — promotion type 1
};

// Same tolerance the pages use: some endpoints nest the body under `data`.
const rootOf = (payload) =>
  (payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object')
    ? payload.data
    : payload;

// Poll interval widens as a job ages. A 40-minute import at a flat 2s is ~1,200
// DB-backed requests, and nobody watches a long import second by second.
// [elapsedMs threshold, interval] — first match from the top wins.
const SLOWEST_INTERVAL = 10000;

const BACKOFF = [
  [60 * 1000, 2000],             // first minute: 2s
  [5 * 60 * 1000, 5000],         // to five minutes: 5s
  [Infinity, SLOWEST_INTERVAL],  // thereafter: 10s
];

function intervalFor(elapsedMs) {
  const step = BACKOFF.find(([until]) => elapsedMs < until);
  return step ? step[1] : SLOWEST_INTERVAL;
}

export default function useJobPoller({
  buildUrl,
  // How long the counters may sit unchanged before we call it stalled. This
  // replaces a wall-clock cap: a job moving through 5,000 rows for 40 minutes is
  // healthy and must never be cut off, while one frozen at 12/1000 is broken and
  // should not take 10 minutes to say so. Elapsed time cannot tell those apart;
  // whether the counters move can.
  stallMs = 3 * 60 * 1000,
  // Absolute backstop, purely so a pathological case cannot poll forever.
  maxDurationMs = 2 * 60 * 60 * 1000,
  maxConsecutiveErrors = 3,
} = {}) {
  const [progress, setProgress] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  const cancelPolling = useCallback(() => {
    stoppedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setIsPolling(false);
  }, []);

  // Unmount: kill the loop WITHOUT setState — that would warn on an unmounted
  // component. cancelPolling is for the still-mounted case.
  useEffect(() => () => {
    stoppedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  // initialProgress seeds the counters for a job adopted mid-flight (get-active-jobs
  // already carries them), so a resumed job shows numbers immediately instead of a
  // bare "Processing…" for one network round-trip.
  const startPolling = useCallback((jobId, { onFinished, onFailed, initialProgress } = {}) => {
    stoppedRef.current = false;
    setProgress(initialProgress || null);
    setIsPolling(true);

    const startedAt = Date.now();
    const deadline = startedAt + maxDurationMs;
    let consecutiveErrors = 0;
    // Stall tracking: the last counter reading we saw, and when it last changed.
    let lastSignature = null;
    let lastMovedAt = startedAt;

    const settle = (fn, arg) => { cancelPolling(); if (fn) fn(arg); };

    async function tick() {
      if (stoppedRef.current) return;
      try {
        const res = await axios.get(buildUrl(jobId));
        if (stoppedRef.current) return;
        consecutiveErrors = 0;

        const root = rootOf(res.data) || {};
        const message = root.message || root.msg || '';

        const rawError = root.error ?? root.Error;
        if (rawError === true || Number(rawError) === 1) {
          return settle(onFailed, {
            reason: JOB_FAILURE.SERVER_ERROR,
            message: message || 'Re-issue failed.',
          });
        }

        const job = root.job;
        if (!job || typeof job !== 'object') {
          return settle(onFailed, {
            reason: JOB_FAILURE.BAD_RESPONSE,
            message: 'Status response contained no job.',
          });
        }

        const totalCount = Number(job.totalCount) || 0;
        const success = Number(job.success) || 0;
        const failed = Number(job.failed) || 0;
        setProgress({ totalCount, success, failed });

        // Any movement resets the stall clock, so a healthy job polls for as
        // long as it needs.
        const signature = `${totalCount}:${success}:${failed}`;
        if (signature !== lastSignature) {
          lastSignature = signature;
          lastMovedAt = Date.now();
        }

        // Number() so a string "1" from the server behaves the same as 1.
        const status = Number(job.status);
        if (status === JOB_STATUS.FINISHED) return settle(onFinished, { job, message });
        if (status === JOB_STATUS.ERROR) {
          return settle(onFailed, {
            reason: JOB_FAILURE.JOB_ERROR,
            message: message || 'The server hit an error while re-issuing.',
            job,
          });
        }
        if (status !== JOB_STATUS.PROCESSING) {
          // Unknown code: stop rather than spin to the deadline on a value we
          // do not understand.
          return settle(onFailed, {
            reason: JOB_FAILURE.BAD_RESPONSE,
            message: `Unexpected job status "${job.status}".`,
            job,
          });
        }

        // Still processing, but nothing has moved for a while. Most likely the
        // worker died without getting to write status 2 — a crash or restart
        // leaves the row at 0 forever, and no amount of further polling will
        // change it.
        if (Date.now() - lastMovedAt >= stallMs) {
          const minutes = Math.round(stallMs / 60000);
          return settle(onFailed, {
            reason: JOB_FAILURE.STALLED,
            message: `No progress for ${minutes} minute${minutes === 1 ? '' : 's'} — `
              + 'this import may have stopped. It may still be running; check back shortly.',
            job,
          });
        }
      } catch (err) {
        if (stoppedRef.current) return;
        consecutiveErrors += 1;
        // One blip must not kill a job that is still running fine.
        if (consecutiveErrors >= maxConsecutiveErrors) {
          return settle(onFailed, { reason: JOB_FAILURE.NETWORK, message: describeError(err) });
        }
      }

      if (Date.now() >= deadline) {
        // Backstop only — a job moving steadily is never cut off by elapsed
        // time, so reaching here means something pathological. Deliberately not
        // phrased as a failure: it may well still be running.
        return settle(onFailed, {
          reason: JOB_FAILURE.TIMEOUT,
          message: 'Still running on the server — stopped watching after 2 hours.',
        });
      }
      timerRef.current = setTimeout(tick, intervalFor(Date.now() - startedAt));
    }

    // First check fires immediately, so counts appear without an initial wait.
    tick();
  }, [buildUrl, stallMs, maxDurationMs, maxConsecutiveErrors, cancelPolling]);

  return { progress, isPolling, startPolling, cancelPolling };
}
